import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { User, AlertCircle, Smartphone } from 'lucide-react';

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#D97706' : '#E5E7EB',
    boxShadow: state.isFocused ? '0 0 0 1px #D97706' : 'none',
    '&:hover': { borderColor: '#D97706' },
    borderRadius: '0.375rem',
    padding: '0.125rem',
    minHeight: '38px', // Taille réduite pour mobile
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 6px',
    flexWrap: 'nowrap', // Évite le débordement en ligne
    overflow: 'auto', // Permet le défilement horizontal
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#FEF3C7',
    borderRadius: '0.25rem',
    padding: '0 0.25rem',
    margin: '2px', // Réduit l'espacement pour mobile
    fontSize: '0.7rem', // Taille réduite pour mobile
    flexShrink: 0, // Empêche la réduction des éléments
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#92400E',
    fontSize: '0.7rem', // Taille réduite pour mobile
    padding: '2px 4px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#92400E',
    padding: '0 4px',
    ':hover': { backgroundColor: '#FEE2E2', color: '#B91C1C' },
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: '0 4px', // Réduit l'espacement des indicateurs
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px', // Réduit l'espacement de l'indicateur
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px', // Réduit l'espacement de l'indicateur
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '0.8rem', // Taille réduite pour le menu déroulant
    zIndex: 9999, // Assure que le menu est au-dessus des autres éléments
  }),
  option: (provided) => ({
    ...provided,
    padding: '8px 12px', // Padding ajusté pour mobile
    fontSize: '0.8rem', // Taille de police réduite
  }),
};

// Composant d'indicateur personnalisé pour mobile
const MobileIndicator = () => (
  <div className="p-1">
    <Smartphone size={14} />
  </div>
);

export default function DeveloperMultiSelect({ options, value, onChange, error }) {
  const [isMobile, setIsMobile] = useState(false);
  
  // Détection de la taille d'écran
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Options formatées pour mobile
  const mobileOptions = options.map(option => ({
    ...option,
    label: option.label.length > 20 ? `${option.label.substring(0, 18)}...` : option.label
  }));

  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
        <User size={14} className="text-terrecuite mr-1" />
        Développeurs
        {isMobile && <span className="ml-1 text-xs text-gray-400">(Mobile)</span>}
      </label>
      
      <div className="relative">
        <Select
          isMulti
          options={isMobile ? mobileOptions : options}
          value={options.filter(o => value.includes(o.value))}
          onChange={selected =>
            onChange({
              target: {
                name: 'developpeur_ids',
                value: selected.map(s => s.value),
              },
            })
          }
          styles={customStyles}
          placeholder={isMobile ? "Choisir..." : "Choisir des développeurs..."}
          className="text-xs"
          classNamePrefix="select"
          // Propriétés pour améliorer l'expérience mobile
          menuPlacement="auto"
          closeMenuOnScroll={true}
          captureMenuScroll={false}
          isSearchable={!isMobile} // Désactiver la recherche sur mobile si nécessaire
          components={isMobile ? { DropdownIndicator: MobileIndicator } : {}}
        />
      </div>
      
      {error && (
        <p className="mt-1 text-xs text-red-500 flex items-center">
          <AlertCircle size={12} className="mr-1" />
          {error}
        </p>
      )}
      
      {/* Indication pour l'utilisation mobile */}
      {isMobile && (
        <p className="mt-1 text-xs text-gray-500">
          Faites défiler horizontalement pour voir les sélections
        </p>
      )}
    </div>
  );
}